import { cn } from "@/lib/utils";
import { Card, Label, MonoMeta, StateWord } from "@/design/primitives";

// The zero-inbox wall's tile grammar. One shape everywhere: a name, a plain
// state line, the counts, a Next line, a blockers line, and a stamp.
//
// Design rules held here (the board's forbidden list): no colored left bars,
// no dark backgrounds, no dots as progress. State is carried by words and
// tone, never by a bar or a dot.

const TONE = { green: "text-os-green", amber: "text-os-amber", red: "text-os-red" };

const BigNumber = ({ number, label, tone = "ink" }) => (
  <div data-testid="email-zero-big-number">
    <div
      className={cn(
        "text-[2.875rem] font-semibold leading-none tracking-[-0.035em] tabular-nums",
        tone === "red" ? "text-os-red" : tone === "amber" ? "text-os-amber" : "text-os-ink",
      )}
    >
      {number}
    </div>
    <Label as="div" className="mt-2">{label}</Label>
  </div>
);

const CountRow = ({ value, label, tone = "ink", testId }) => (
  <div className="flex items-baseline gap-2" data-testid={testId}>
    <span
      className={cn(
        "text-[1rem] font-semibold tabular-nums",
        tone === "red" ? "text-os-red" : tone === "amber" ? "text-os-amber" : "text-os-ink",
      )}
    >
      {value}
    </span>
    <span className="text-[0.8125rem] text-os-ink-2">{label}</span>
  </div>
);

// One brand tile (or the unsorted tile — the same shape, always shown).
export const EmailZeroTile = ({ card }) => {
  const p = card.payload || {};
  const {
    title, clean, needsReply, callBacks, waitingLonger, old,
    answered, bounced, stateWord, stateTone, stateLine, next, blockers, latest,
  } = p;
  const tone = TONE[stateTone] || "text-os-ink-3";
  const waiting = (waitingLonger || 0) + (old || 0);

  return (
    <Card
      quiet={!clean && !waiting}
      data-testid={`email-zero-tile-${card.slug}`}
      data-state={card.state}
      aria-label={`${title}. ${stateLine}`}
      className="flex h-full flex-col p-5 text-left"
    >
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="min-w-0 truncate text-[0.9375rem] font-semibold tracking-[-0.01em] text-os-ink">
          {title}
        </h3>
        <StateWord tone={stateTone}>{stateWord}</StateWord>
      </div>
      <p data-testid={`email-zero-tile-line-${card.slug}`} className={cn("mt-3 text-[0.875rem] leading-[1.45]", tone)}>
        {stateLine}
      </p>

      {waiting > 0 && (
        <p
          data-testid={`email-zero-tile-longer-${card.slug}`}
          className="mt-2 text-[0.8125rem] leading-[1.4] text-os-amber"
        >
          {waitingLonger > 0 && `${waitingLonger} still open — waiting longer`}
          {waitingLonger > 0 && old > 0 && " · "}
          {old > 0 && `${old} waiting — old`}
        </p>
      )}

      <div className="mt-4 border-t border-os-border pt-4">
        <BigNumber number={clean} label={clean === 1 ? "needs a reply" : "need a reply"} />
        <div className="mt-4 grid grid-cols-2 gap-3">
          <CountRow value={needsReply} label="needs a reply" tone="red" testId={`email-zero-tile-reply-${card.slug}`} />
          <CountRow value={callBacks} label="call-backs owed" tone={callBacks > 0 ? "red" : "ink"} testId={`email-zero-tile-callback-${card.slug}`} />
          <CountRow value={waitingLonger} label="waiting longer" tone="amber" testId={`email-zero-tile-waiting-${card.slug}`} />
          <CountRow value={answered} label="answered" tone="ink" />
          <CountRow value={bounced} label="couldn't deliver" tone={bounced > 0 ? "red" : "ink"} />
        </div>
      </div>

      {latest && (
        <div className="mt-3 border-t border-os-border pt-3">
          <p className="text-[0.8125rem] leading-[1.45] text-os-ink">
            Latest: <span className="text-os-ink-2">{latest.subject}</span>
          </p>
          <p className="mt-1 text-[0.75rem] text-os-ink-3">
            {latest.person} · {latest.cameIn}
          </p>
        </div>
      )}

      <div className="mt-3 flex flex-1 flex-col gap-2">
        <p data-testid={`email-zero-tile-next-${card.slug}`} className="text-[0.875rem] font-medium leading-[1.45] text-os-ink">
          {next}
        </p>
        <p data-testid={`email-zero-tile-blockers-${card.slug}`} className="text-[0.8125rem] leading-[1.4] text-os-ink-3">
          {blockers}
        </p>
      </div>
    </Card>
  );
};

export default EmailZeroTile;
